import { useState, useCallback } from "react";

import type { TrialPlan } from "../views/TrialPlanning/types";

const STORAGE_KEY = "vivo_promoted_trials";

export interface PromotedTrial {
  planId: string;
  name: string;
  indication: string;
  phase: string;
  sponsor?: string;
  description?: string;
  therapeutic_area?: string | null;
  status: "Planned";
  promotedAt: string;
  /** `updatedAt` of the plan at the moment it was published — identifies which
   *  version of the plan is the one live in Vivo. Absent on entries published
   *  before versioned publishing existed. */
  sourceVersion?: string;
  /** Frozen copy of the plan exactly as published. Later edits in Trial
   *  Planning never touch this — republishing replaces it wholesale. */
  snapshot?: TrialPlan;
}

function load(): PromotedTrial[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PromotedTrial[]) : [];
  } catch {
    return [];
  }
}

function persist(trials: PromotedTrial[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(trials));
}

export function usePromotedTrials() {
  const [trials, setTrials] = useState<PromotedTrial[]>(() => load());

  /** Publish a plan into Vivo as a planned trial. Upserts: republishing an
   *  already-promoted plan replaces its snapshot with the plan's current
   *  version, so "Update in Vivo" is the same call as the first publish. */
  const promote = useCallback((plan: TrialPlan) => {
    const entry: PromotedTrial = {
      planId: plan.id,
      name: plan.name,
      indication: plan.indication,
      phase: plan.phase,
      sponsor: plan.sponsor,
      description: plan.overview,
      therapeutic_area: null,
      status: "Planned",
      promotedAt: new Date().toISOString(),
      sourceVersion: plan.updatedAt,
      snapshot: JSON.parse(JSON.stringify(plan)) as TrialPlan,
    };
    const updated = [...load().filter((t) => t.planId !== plan.id), entry];
    persist(updated);
    setTrials(updated);
  }, []);

  const isPromoted = useCallback(
    (planId: string) => trials.some((t) => t.planId === planId),
    [trials],
  );

  const getPromoted = useCallback(
    (planId: string) => trials.find((t) => t.planId === planId),
    [trials],
  );

  const remove = useCallback((planId: string) => {
    const updated = load().filter((t) => t.planId !== planId);
    persist(updated);
    setTrials(updated);
  }, []);

  return { trials, promote, isPromoted, getPromoted, remove };
}
